import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import { create, show, useModal } from "@ebay/nice-modal-react";
import { useNavigate } from "react-router";

import { Collapse, Expand } from "components/icons/generated";
import FlashContext from "components/FlashMessages/FlashContext";
import Banner from "ds/components/Banner";
import Box from "ds/components/Box";
import Button from "ds/components/Button";
import ButtonIcon from "ds/components/ButtonIcon";
import CardCollapsible from "ds/components/Card/Collapsible";
import Counter from "ds/components/Counter";
import ModalCancelButton from "ds/components/Modal/CancelButton";
import ModalContent from "ds/components/Modal/Content";
import ModalFooter from "ds/components/Modal/Footer";
import ModalHeader from "ds/components/Modal/Header";
import ModalSimple from "ds/components/ModalSimple";
import { useToggle } from "hooks/useToggle";
import useTypedContext from "hooks/useTypedContext";
import { BlueprintState } from "types/generated";
import { CREATE_TEMPLATE } from "views/Account/TemplatesWorkbench/gql";
import { CREATE_VERSION } from "views/Account/Template/Versions/gql";
import ReadMoreDocsLink from "components/ReadMoreDocsLink";
import { getDocsUrl } from "utils/getDocsUrl";

import ExportToTemplateDiffEditor from "./DiffEditor";

type ExportToTemplateModalProps = {
  blueprintName: string;
  blueprintDescription?: string | null;
  blueprintSpaceId: string;
  blueprintLabels: string[];
  originalTemplate: string;
  migratedTemplate: string;
  warnings: string[];
  errors: string[];
};

const ExportToTemplateModal = create(function ExportToTemplateModal({
  blueprintName,
  blueprintDescription,
  blueprintSpaceId,
  blueprintLabels,
  originalTemplate,
  migratedTemplate,
  warnings,
  errors,
}: ExportToTemplateModalProps) {
  const modal = useModal();
  const navigate = useNavigate();
  const { onError, reportSuccess, reportError } = useTypedContext(FlashContext);
  const [isExporting, setIsExporting] = useState(false);
  const [isFullscreen, toggleFullscreen] = useToggle(false);
  const [isWarningsCollapsed, toggleWarnings] = useToggle(false);
  const [isErrorsCollapsed, toggleErrors] = useToggle(false);

  const [createTemplate] = useMutation(CREATE_TEMPLATE);
  const [createVersion] = useMutation(CREATE_VERSION);

  const handleExport = async () => {
    setIsExporting(true);

    try {
      const { data: groupData } = await createTemplate({
        variables: {
          input: {
            name: blueprintName,
            space: blueprintSpaceId,
            description: blueprintDescription || "",
            labels: blueprintLabels,
            initialVersion: null,
          },
        },
      });

      const groupId = groupData?.templateCreate?.id;

      if (!groupId) {
        reportError({ message: "Failed to create template" });
        return;
      }

      const { data: versionData } = await createVersion({
        variables: {
          input: {
            templateID: groupId,
            state: BlueprintState.Draft,
            template: migratedTemplate,
            versionNumber: "0.1",
            labels: [],
            instructions: "",
          },
        },
      });

      const versionId = versionData?.templateVersionCreate?.id;

      if (!versionId) {
        reportError({ message: "Failed to create template version" });
        return;
      }

      reportSuccess({
        message: `Template "${blueprintName}" successfully created`,
      });

      modal.hide();
      navigate(`/template/${groupId}/version/${versionId}`);
    } catch (error) {
      onError(error);
    } finally {
      setIsExporting(false);
    }
  };

  const hasWarnings = warnings.length > 0;
  const hasErrors = errors.length > 0;

  return (
    <ModalSimple size={isFullscreen ? "fullscreen" : "large"}>
      <ModalHeader
        title="Export to template"
        suffix={
          <ButtonIcon
            icon={isFullscreen ? Collapse : Expand}
            onPress={() => toggleFullscreen()}
            variant="ghost"
          >
            {isFullscreen ? "Exit fullscreen" : "Fullscreen"}
          </ButtonIcon>
        }
      />
      <ModalContent gap="x-large">
        <ExportToTemplateDiffEditor
          originalTemplate={originalTemplate}
          migratedTemplate={migratedTemplate}
          warnings={warnings}
          errors={errors}
        />

        {hasWarnings && (
          <Box direction="column" gap="large">
            <CardCollapsible
              ariaLevel={3}
              ariaLabel="Migration warnings"
              title="Migration warnings"
              isCollapsed={isWarningsCollapsed}
              onToggle={toggleWarnings}
              titleSuffix={<Counter count={warnings.length} />}
              contentGap="medium"
            >
              Some blueprint features are not fully compatible with the new template format. Review
              the changes below and adjust the exported template as needed after the export.{" "}
              <ReadMoreDocsLink
                docsUrl={getDocsUrl(
                  "/concepts/template/configuration#templateschema-key-differences"
                )}
              />
              {warnings.map((warning, index) => (
                <Banner key={index} variant="warning">
                  {warning}
                </Banner>
              ))}
            </CardCollapsible>
          </Box>
        )}
        {hasErrors && (
          <Box direction="column" gap="large">
            <CardCollapsible
              ariaLevel={3}
              ariaLabel="Migration errors"
              title="Migration errors"
              isCollapsed={isErrorsCollapsed}
              onToggle={toggleErrors}
              titleSuffix={<Counter count={errors.length} />}
              contentGap="medium"
            >
              Some blueprint features are not compatible with the new template format and were not
              migrated. Review the errors below and adjust the exported template as needed after the
              export.{" "}
              <ReadMoreDocsLink
                docsUrl={getDocsUrl(
                  "/concepts/template/configuration#templateschema-key-differences"
                )}
              />
              {errors.map((error, index) => (
                <Banner key={index} variant="danger">
                  {error}
                </Banner>
              ))}
            </CardCollapsible>
          </Box>
        )}
      </ModalContent>
      <ModalFooter>
        <ModalCancelButton onClick={modal.hide} />
        <Button variant="primary" onPress={handleExport} loading={isExporting}>
          Export to template
        </Button>
      </ModalFooter>
    </ModalSimple>
  );
});

export const showExportToTemplateModal = (props: ExportToTemplateModalProps) =>
  show(ExportToTemplateModal, props);
